import moment from "moment";

export interface DateTreeFilterSetupOptions {
  rows: any[];
  fields: any[];
  hints: string[];
}

export interface DateTreeFilterHelperOptions {
  getTranslation: () => any;
  getPopoverElement: () => HTMLElement;
  getGridElement: () => HTMLElement;
  applyRows: (rows: any[]) => void;
  parseDateValue?: (value: any) => Date;
  dateFormats?: string[];
}

export interface RsCommonGridDateTreeFactoryHost {
  translation: any;
  dateTreePopoverRef: any;
  grid: any;
}

export const DATE_TREE_DEFAULT_DATE_FORMATS = [
  "YYYY-MM-DD",
  "MM/DD/YYYY",
  "DD/MM/YYYY",
  "YYYY/MM/DD",
  "DD-MMM-YYYY",
  "MMM DD, YYYY",
];

export class DateTreeFilterHelper {
  public popoverVisible = false;
  public popoverField = "";
  public popoverStyle = {};
  public selectedByField = {};
  public fieldLabelMap = {};
  public dateFields = [];

  private sourceRows = [];
  private options: DateTreeFilterHelperOptions;
  private defaultDateFormats = [
    "YYYY-MM-DD",
    "MM/DD/YYYY",
    "DD/MM/YYYY",
    "YYYY/MM/DD",
    "DD-MMM-YYYY",
    "DD-MMM-YYYY hh:mm A",
    "MMM DD, YYYY",
    moment.ISO_8601,
  ];

  constructor(options: DateTreeFilterHelperOptions) {
    this.options = options;
  }

  public setup(config: DateTreeFilterSetupOptions) {
    var rows = config && config.rows ? config.rows : [];
    var fields = config && config.fields ? config.fields : [];
    var hints = config && config.hints ? config.hints : [];
    this.sourceRows = rows;
    this.dateFields = this.resolveDateTreeFieldsByHints(fields, hints);
    this.fieldLabelMap = {};
    (fields || []).forEach((item) => {
      if (!item || !item.columnName) {
        return;
      }
      this.fieldLabelMap[item.columnName] =
        item.columnDisplayName || item.columnName;
    });
    this.apply();
  }

  public handleGridActionBegin(args: any) {
    var requestType = this.getRequestType(args);
    if (
      requestType === "filterchoicerequest" ||
      requestType === "filtersearchbegin"
    ) {
      args.filterChoiceCount = 15000;
    }
    if (requestType !== "filterbeforeopen") {
      return;
    }
    var columnName = this.resolveActionColumnName(args);
    if (!this.isDateTreeField(columnName)) {
      this.close();
      return;
    }
    args.cancel = true;
    this.open(columnName);
  }

  public updateSelection(selectionMap: any) {
    this.selectedByField = selectionMap || {};
    this.apply();
  }

  public close() {
    if (!this.popoverVisible) {
      return;
    }
    this.popoverVisible = false;
    this.popoverField = "";
    this.popoverStyle = {};
  }

  public handleDocumentMouseDown(event: MouseEvent) {
    if (!this.popoverVisible) {
      return;
    }
    var target = event.target as HTMLElement;
    if (!target) {
      return;
    }
    if (this.isInsidePopover(target) || this.isDateFilterMenuItem(target)) {
      return;
    }
    this.close();
  }

  public handleDocumentMouseOver(event: MouseEvent) {
    if (!this.popoverVisible) {
      return;
    }
    var target = event.target as HTMLElement;
    if (!target) {
      return;
    }
    if (this.isInsidePopover(target)) {
      return;
    }
    var menuItem = target.closest(".e-menu-item") as HTMLElement;
    if (menuItem) {
      if (!this.isDateFilterMenuItem(menuItem)) {
        this.close();
      }
      return;
    }
    var otherFilterTarget = target.closest(
      ".e-filtermenudiv, .e-headercell .e-columnmenu, .e-columnmenu, .e-contextmenu"
    ) as HTMLElement;
    if (otherFilterTarget && !this.isDateFilterMenuItem(otherFilterTarget)) {
      this.close();
    }
  }

  public updatePosition() {
    if (!this.popoverVisible) {
      return;
    }
    var anchor = this.findDateFilterAnchorElement();
    if (anchor) {
      var rect = anchor.getBoundingClientRect();
      var viewportWidth =
        window.innerWidth || document.documentElement.clientWidth;
      var viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      var popoverElement = this.options.getPopoverElement
        ? this.options.getPopoverElement()
        : null;
      var popoverWidth =
        popoverElement && popoverElement.offsetWidth
          ? popoverElement.offsetWidth
          : 300;
      var popoverHeight =
        popoverElement && popoverElement.offsetHeight
          ? popoverElement.offsetHeight
          : 420;
      var edgeGap = 8;
      var anchorGap = 8;
      var top = rect.top - 6;
      var left = rect.right + anchorGap;

      if (left + popoverWidth > viewportWidth - edgeGap) {
        left = rect.left - popoverWidth - anchorGap;
      }
      if (left < edgeGap) {
        left = edgeGap;
      }
      if (left + popoverWidth > viewportWidth - edgeGap) {
        left = Math.max(edgeGap, viewportWidth - popoverWidth - edgeGap);
      }
      if (top + popoverHeight > viewportHeight - edgeGap) {
        top = Math.max(edgeGap, viewportHeight - popoverHeight - edgeGap);
      }
      this.popoverStyle = {
        position: "fixed",
        left: left + "px",
        top: top + "px",
      };
      return;
    }
    var gridElement = this.options.getGridElement
      ? this.options.getGridElement()
      : null;
    if (gridElement && gridElement.getBoundingClientRect) {
      var fallbackRect = gridElement.getBoundingClientRect();
      this.popoverStyle = {
        position: "fixed",
        left: Math.max(8, fallbackRect.right - 312) + "px",
        top: Math.max(8, fallbackRect.top + 8) + "px",
      };
    }
  }

  private open(field: string) {
    this.popoverField = field;
    this.popoverVisible = true;
    this.updatePosition();
    setTimeout(() => this.updatePosition(), 50);
    setTimeout(() => this.updatePosition(), 150);
  }

  private apply() {
    var filteredRows = this.filterRowsBySelectedDateTree(
      this.sourceRows || []
    );
    this.options.applyRows(filteredRows);
  }

  private getRequestType(args) {
    if (!args || !args.requestType) {
      return "";
    }
    return String(args.requestType).trim().toLowerCase();
  }

  private resolveActionColumnName(args) {
    if (!args) {
      return "";
    }
    return (
      args.columnName ||
      args.field ||
      args.currentFilteringColumn ||
      (args.column && (args.column.field || args.column.columnName)) ||
      ""
    );
  }

  private resolveDateTreeFieldsByHints(fields: any[], hints: string[]) {
    var normalizedHints = (hints || [])
      .filter((item) => !!item)
      .map((item) => String(item).trim().toLowerCase());
    if (!fields || !fields.length || !normalizedHints.length) {
      return [];
    }
    return fields
      .filter((item) => {
        if (!item || !item.columnName) {
          return false;
        }
        var columnName = String(item.columnName || "").toLowerCase();
        var displayName = String(item.columnDisplayName || "").toLowerCase();
        return normalizedHints.some((hint) => {
          return (
            columnName === hint ||
            displayName === hint ||
            (hint.length >= 3 &&
              (columnName.indexOf(hint) > -1 ||
                displayName.indexOf(hint) > -1))
          );
        });
      })
      .map((item) => item.columnName);
  }

  private isDateTreeField(field: string) {
    return this.dateFields.indexOf(field) > -1;
  }

  private getFieldDaySelectionMap(field: string) {
    if (!this.selectedByField[field]) {
      this.selectedByField[field] = {};
    }
    return this.selectedByField[field];
  }

  private filterRowsBySelectedDateTree(rows: any[], excludeField?: string) {
    var filteredRows = rows ? rows.slice() : [];
    this.dateFields.forEach((field) => {
      if (excludeField && field === excludeField) {
        return;
      }
      var selectedMap = this.getFieldDaySelectionMap(field);
      var selectedKeys = Object.keys(selectedMap);
      if (!selectedKeys.length) {
        return;
      }
      filteredRows = filteredRows.filter((row) => {
        var dayKey = this.getDayKeyByRowValue(row[field]);
        return dayKey ? !!selectedMap[dayKey] : false;
      });
    });
    return filteredRows;
  }

  private getDayKeyByRowValue(value: any) {
    var date = this.parseDateValue(value);
    if (!date) {
      return "";
    }
    return this.toDayKey(
      date.getFullYear(),
      date.getMonth() + 1,
      date.getDate()
    );
  }

  private parseDateValue(value: any): Date {
    if (this.options.parseDateValue) {
      return this.options.parseDateValue(value);
    }
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    var formats =
      this.options && this.options.dateFormats && this.options.dateFormats.length
        ? this.options.dateFormats
        : this.defaultDateFormats;
    var parsed = moment(value, formats, true);
    if (parsed.isValid()) {
      return parsed.toDate();
    }
    var fallback = new Date(value);
    return isNaN(fallback.getTime()) ? null : fallback;
  }

  private toDayKey(year: number, month: number, day: number) {
    return year + "-" + this.pad2(month) + "-" + this.pad2(day);
  }

  private pad2(value: number) {
    return value < 10 ? "0" + value : String(value);
  }

  private isInsidePopover(target: HTMLElement): boolean {
    var popoverElement = this.options.getPopoverElement
      ? this.options.getPopoverElement()
      : null;
    if (!target || !popoverElement) {
      return false;
    }
    return popoverElement.contains(target);
  }

  private isDateFilterMenuItem(element: HTMLElement): boolean {
    if (!element) {
      return false;
    }
    var menuItem = element.closest(".e-menu-item") as HTMLElement;
    if (!menuItem) {
      return false;
    }
    var text = (menuItem.textContent || "").trim().toLowerCase();
    var translation = this.options.getTranslation
      ? this.options.getTranslation()
      : null;
    var filterText = String((translation && translation.FILTER) || "filter")
      .trim()
      .toLowerCase();
    if (!text) {
      return false;
    }
    if (text.indexOf(filterText) > -1 || text.indexOf("filter") > -1) {
      return true;
    }
    var cls = (menuItem.className || "").toLowerCase();
    return cls.indexOf("filter") > -1;
  }

  private findDateFilterAnchorElement(): HTMLElement {
    var menuContainers = Array.from(
      document.querySelectorAll(".e-columnmenu, .e-contextmenu")
    ) as HTMLElement[];
    var visibleContainers = menuContainers.filter((container) => {
      if (!container || !container.getBoundingClientRect) {
        return false;
      }
      var rect = container.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return false;
      }
      var style = window.getComputedStyle(container);
      if (!style || style.display === "none" || style.visibility === "hidden") {
        return false;
      }
      return true;
    });
    if (!visibleContainers.length) {
      return null;
    }
    var currentContainer = visibleContainers[visibleContainers.length - 1];
    var menuItems = Array.from(
      currentContainer.querySelectorAll(".e-menu-item")
    ) as HTMLElement[];
    var visibleFilterItems = menuItems.filter((item) => {
      if (!this.isDateFilterMenuItem(item)) {
        return false;
      }
      if (!item.getBoundingClientRect) {
        return false;
      }
      var rect = item.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    });
    if (!visibleFilterItems.length) {
      return null;
    }
    var focusedItem = visibleFilterItems.find((item) => {
      return (
        item.classList.contains("e-focused") ||
        item.classList.contains("e-selected") ||
        item.classList.contains("e-hover")
      );
    });
    return focusedItem || visibleFilterItems[visibleFilterItems.length - 1];
  }
}

function resolveGridElement(host: RsCommonGridDateTreeFactoryHost): HTMLElement {
  if (!host || !host.grid) {
    return null;
  }
  var gridRef = host.grid;
  if (gridRef.grid && gridRef.grid.element) {
    return gridRef.grid.element;
  }
  if (gridRef.element) {
    return gridRef.element;
  }
  return null;
}

export function createRsCommonGridDateTreeHelper(
  host: RsCommonGridDateTreeFactoryHost,
  applyRows: (rows: any[]) => void,
  dateFormats?: string[]
) {
  return new DateTreeFilterHelper({
    getTranslation: () => (host ? host.translation : null),
    getPopoverElement: () =>
      host && host.dateTreePopoverRef
        ? host.dateTreePopoverRef.nativeElement
        : null,
    getGridElement: () => resolveGridElement(host),
    applyRows: applyRows,
    dateFormats:
      dateFormats && dateFormats.length
        ? dateFormats
        : DATE_TREE_DEFAULT_DATE_FORMATS,
  });
}
